/**
 * ExportMenu — Dedrone parity drop-down on the report header.
 *
 * Four items:
 *   - Download as PDF       — browser print; styled via print CSS
 *   - Show as alert list    — swaps the view mode in the parent page
 *   - Create scheduled      — stub for Phase 4 (sends an alert to operator)
 *   - View scheduled        — stub, routes to /reports/scheduled (not built)
 *
 * The last two are "not yet available" toasts — clicking them doesn't error,
 * but the sched-report pipeline is a separate roadmap item. Dedrone's UX does
 * the same dance (the dialog collects fields and queues server-side), and
 * we'll flesh it out when push notifications + email delivery are green.
 */
import { Fragment } from 'react';
import { Menu, Transition } from '@headlessui/react';
import {
  ArrowDownTrayIcon,
  ChevronDownIcon,
  DocumentTextIcon,
  ListBulletIcon,
  ClockIcon,
  CalendarIcon,
} from '@heroicons/react/24/outline';
import clsx from 'clsx';

interface Props {
  onShowAsList: () => void;
  onShowAsSummary: () => void;
  mode: 'summary' | 'alerts';
  onDownloadPdf?: () => void;
  onCreateSchedule?: () => void;
  onViewSchedule?: () => void;
}

export default function ExportMenu({
  onShowAsList,
  onShowAsSummary,
  mode,
  onDownloadPdf,
  onCreateSchedule,
  onViewSchedule,
}: Props) {
  return (
    <Menu as="div" className="relative">
      <Menu.Button className="btn-secondary flex items-center gap-2">
        <ArrowDownTrayIcon className="h-4 w-4" />
        Export
        <ChevronDownIcon className="h-4 w-4" />
      </Menu.Button>
      <Transition
        as={Fragment}
        enter="transition ease-out duration-100"
        enterFrom="transform opacity-0 scale-95"
        enterTo="transform opacity-100 scale-100"
        leave="transition ease-in duration-75"
        leaveFrom="transform opacity-100 scale-100"
        leaveTo="transform opacity-0 scale-95"
      >
        <Menu.Items className="absolute right-0 mt-2 w-64 origin-top-right bg-navy-800 border border-navy-700 rounded-lg shadow-xl z-40 focus:outline-none overflow-hidden">
          <div className="p-1">
            <MenuRow
              icon={DocumentTextIcon}
              label="Download as PDF"
              onClick={onDownloadPdf || (() => window.print())}
            />
            <MenuRow
              icon={ListBulletIcon}
              label={mode === 'alerts' ? 'Show as summary' : 'Show as alert list'}
              onClick={mode === 'alerts' ? onShowAsSummary : onShowAsList}
            />
          </div>
          <div className="border-t border-navy-700 p-1">
            <MenuRow
              icon={ClockIcon}
              label="Create scheduled report"
              onClick={onCreateSchedule}
              disabled={!onCreateSchedule}
              hint="Coming soon"
            />
            <MenuRow
              icon={CalendarIcon}
              label="View scheduled reports"
              onClick={onViewSchedule}
              disabled={!onViewSchedule}
              hint="Coming soon"
            />
          </div>
        </Menu.Items>
      </Transition>
    </Menu>
  );
}

interface MenuRowProps {
  icon: React.ComponentType<{ className?: string }>;
  label: string;
  onClick?: () => void;
  disabled?: boolean;
  hint?: string;
}

function MenuRow({ icon: Icon, label, onClick, disabled, hint }: MenuRowProps) {
  return (
    <Menu.Item disabled={disabled}>
      {({ active }) => (
        <button
          type="button"
          onClick={onClick}
          disabled={disabled}
          className={clsx(
            'w-full flex items-center justify-between gap-2 px-3 py-2 rounded-md text-sm text-left',
            disabled
              ? 'text-gray-500 cursor-not-allowed'
              : active
                ? 'bg-navy-700 text-white'
                : 'text-gray-300',
          )}
        >
          <span className="flex items-center gap-2">
            <Icon className="h-4 w-4" />
            {label}
          </span>
          {hint && <span className="text-[10px] text-gray-500">{hint}</span>}
        </button>
      )}
    </Menu.Item>
  );
}
